import { Operation } from "@apollo/client";
import { NetworkError } from "@apollo/client/errors";
import * as Sentry from "@sentry/react";
import { isString, toLower, uniqBy } from "lodash";

import { errorNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";

export type ErrorMatchType = {
  match: string;
  title: string;
  description?: string;
};

export const findErrorMatch = (
  message: string,
  errorMatches: Array<ErrorMatchType>,
) =>
  errorMatches.find(({ match }) =>
    String(message).toLowerCase().includes(match.toLowerCase()),
  );

// The GraphQL errors that no errorMatch handles, one per message. Each one goes
// to Sentry: a field that fails on every item of a list is still one event.
export const unexpectedGraphqlErrors = <T extends { message: string }>(
  graphQLErrors: ReadonlyArray<T>,
  errorMatches: Array<ErrorMatchType>,
) =>
  uniqBy(
    graphQLErrors.filter(
      (graphQLError) => !findErrorMatch(graphQLError.message, errorMatches),
    ),
    "message",
  );

export const defaultNetworkErrorHandler = (
  networkError: NetworkError,
  operation?: Operation,
) => {
  if (!networkError) return;

  if ("statusCode" in networkError && networkError.statusCode === 401) {
    const loginUrl = `/login?next=${window.location.pathname}`;
    errorNotificationVar({
      timeout: null,
      title: "Your session has expired.",
      description: (
        <div>
          Please refresh the page and{" "}
          <a href={loginUrl} className="underline text-blue-600">
            log in
          </a>
          .
          <div className="mt-4">
            <Button url={loginUrl} theme={buttonTheme.primary} text="Login" />
          </div>
        </div>
      ),
    });
  } else if (
    "statusCode" in networkError &&
    networkError.statusCode === 403 &&
    "result" in networkError &&
    isString(networkError.result) &&
    networkError.result.toLowerCase().includes("csrf")
  ) {
    errorNotificationVar({
      timeout: null,
      title: "This page is out of date",
      description: (
        <div>Please make note of any unsaved work and refresh to continue</div>
      ),
    });
  } else if (toLower(String(networkError)).includes("failed to fetch")) {
    errorNotificationVar({
      description: "Make sure you are connected to the internet and try again.",
    });
  } else {
    errorNotificationVar({
      description: `An unexpected error occurred. We have been notified. Please try again.${
        window.Cypress ? JSON.stringify(networkError) : ""
      }`,
    });
    Sentry.captureException(
      new Error(
        `[Network error] ${operation?.operationName || ""}: ${networkError}`,
      ),
      {
        extra: {
          operationName: operation?.operationName,
          variables: operation?.variables,
          error: networkError,
        },
      },
    );
  }
};
